import React, { useEffect, useState } from 'react'

import styled from 'styled-components'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'

import { Panel } from 'components/Panel'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Table from 'components/Table'

import ComponentList from 'pages/Device/SystemComponentsPanel/ComponentList'
import LastUpdated from 'pages/Device/LastUpdated'

import useNexiaStream from 'hooks/useNexiaStream'
import useScreenSize from 'hooks/useScreenSize'

import { NoData, PanelHeaderSecondaryButton, screenSize } from 'styles/shared'

import Apptentive from 'helpers/apptentive'
import RunReportModal from 'pages/Device/SystemComponentsPanel/RunReportModal'
import { color } from 'styles/colors'

import { useWebsocketContext } from 'hooks/WebsocketContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import ExtendedDisconnect from 'pages/shared/Device/ExtendedDisconnect'

export type Props = {
  deviceId: string
  reportCache: object
  readOnly: boolean
  communicating: boolean
}

const SYSTEM_COMPONENTS = 'System Components'

const SystemComponentsPanel = ({ deviceId, communicating, reportCache, readOnly }: Props) => {
  const screenSize = useScreenSize()

  const { eventData: report } = useNexiaStream(deviceId, 'sys_components', reportCache)
  const [totalComponents, setTotalComponents] = useState(0)
  const [isRunReportOpen, setIsRunReportOpen] = useState(false)

  const { enabledFeatures } = useFeaturesContext()
  const { subscribe, unsubscribe, payload: communicatingComponentsReport } = useWebsocketContext(deviceId, 'communicating_components')
  const isWebsocketEnabled = enabledFeatures.includes('montblanc_websockets')
  const actualReport = isWebsocketEnabled ? communicatingComponentsReport : report
  const extendedDisconnect = isWebsocketEnabled ? communicatingComponentsReport?.extendedDisconnect : false

  const isLoaded = actualReport !== null

  useEffect(() => {
    subscribe(deviceId, 'communicating_components')

    return () => {
      unsubscribe(deviceId, 'communicating_components')
    }
  }, [deviceId, subscribe, unsubscribe])

  useEffect(() => {
    if (actualReport) {
      const componentsCount = (actualReport.components || []).length
      setTotalComponents(componentsCount)
      sendApptentiveEvent()
    }
  }, [actualReport]) // eslint-disable-line react-hooks/exhaustive-deps

  const sendApptentiveEvent = () => {
    let hasRelayPanel = false
    const components = actualReport?.components || []

    components.forEach((component) => {
      if (component.isRelayPanel === true) {
        hasRelayPanel = true
      }
    })

    if (hasRelayPanel && communicating) {
      Apptentive.sendEvent('viewed_hybrid_system_details')
    } else if (communicating) {
      Apptentive.sendEvent('viewed_communicating_system_details')
    } else {
      Apptentive.sendEvent('viewed_24v_system_details')
    }
  }

  const onUpdate = (componentsCount: number) => {
    setTotalComponents(componentsCount)
  }

  if (extendedDisconnect) {
    return (
      <ExtendedDisconnect title={SYSTEM_COMPONENTS} type="normal" />
    )
  }

  return (
    <S.Panel pingdomId="sys-components-container" id="system-components" scrollToSection>
      <S.PanelHeader>
        <S.PanelHeaderLeft>
          <S.PanelTitle>{totalComponents && screenSize !== 'small' && screenSize !== 'medium' ? SYSTEM_COMPONENTS + ' (' + `${totalComponents}` + ')' : SYSTEM_COMPONENTS}</S.PanelTitle>
          <Button decoration="primary" styles={PanelHeaderSecondaryButton} onPress={() => setIsRunReportOpen(true)}><S.ButtonText><S.DownloadIcon />Warranty Report</S.ButtonText></Button>
        </S.PanelHeaderLeft>
        <RunReportModal
          deviceId={deviceId}
          isOpen={isRunReportOpen}
          onClose={() => setIsRunReportOpen(false)}
        />
        {actualReport && <LastUpdated date={actualReport.lastUpdatedAt} />}
      </S.PanelHeader>
      {
        !isLoaded
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <S.Content>
              <S.TableHeader>
                <Table.HeaderCell title="Description" />
                <Table.HeaderCell title="Model Number" />
                <Table.HeaderCell title="Serial Number" />
                <Table.HeaderCell title="Version Number" />
                <Table.HeaderCell
                  title="Device Reporting Status"
                  tooltip="Identifies if a device is currently reporting on the communication bus"
                />
                <Table.HeaderCell title="Warranty Status" />
              </S.TableHeader>
              <ComponentList components={actualReport.components || []} deviceId={deviceId} onUpdateCallBack={onUpdate} readOnly={readOnly} />
            </S.Content>
          )
      }
    </S.Panel>
  )
}

export default SystemComponentsPanel

const S = {
  Content: styled.div`
    overflow-wrap: anywhere;
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  Panel: styled(Panel)`
    scroll-margin-top: 60px;

    @media ${screenSize.small}, ${screenSize.medium} {
      width: 100%;
    }
  `,
  TableHeader: styled(Table.Header)`
    display: grid;
    grid-template-columns: 18% 18% 15% 18% 15% auto;

    @media ${screenSize.small} , ${screenSize.medium} {
      display: none;
    }
  `,
  PanelHeaderLeft: styled(Panel.Header.Left)`
    display: flex;
    align-items: center;
    gap: 10px;
    @media ${screenSize.small} {
      gap: 0px;
    }
  `,
  PanelHeader: styled(Panel.Header)`
    padding-right: 4px;
  `,
  ButtonText: styled.div`
    color: ${color('primaryButton')};
    margin-top: 2px;
    font-weight: 700;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 3px;
    &:hover {
      color: ${color('primaryButtonHover')};
    }
  `,
  DownloadIcon: styled(DownloadIcon)`
    font-size: 1.25em;
  `,
  PanelTitle: styled(Panel.Title)`
    @media ${screenSize.small}, ${screenSize.medium} {
     font-size: 1.125em;
    }
  `
}
